import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './StudyInterface.module.css';

const StudyInterface = ({ notes, cards }) => {
    const [currentCard, setCurrentCard] = useState(0);
    const [score, setScore] = useState(0);
    const [notesHtml, setNotesHtml] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Process notes when they change
        if (notes) {
            try {
                // Configure marked options
                marked.setOptions({
                    gfm: true,
                    breaks: true,
                    sanitize: false // We'll use DOMPurify instead
                });
                
                // Convert markdown to HTML and sanitize
                const htmlContent = DOMPurify.sanitize(marked(notes));
                setNotesHtml(htmlContent);
            } catch (error) {
                console.error('Error processing notes:', error);
                setNotesHtml('<p>Error processing study notes. Please try again.</p>');
            }
        }
        
        // Reset state when cards change
        if (cards && cards.length > 0) {
            setCurrentCard(0);
            setScore(0);
        }
        
        setLoading(false);
    }, [notes, cards]);

    const handleSwipe = (isRight) => {
        if (!cards || !cards[currentCard]) return;
        
        const card = cards[currentCard];
        if (card.correct === isRight) {
            setScore(score + 1);
        }

        if (currentCard < cards.length - 1) {
            setCurrentCard(prev => prev + 1);
        }
    };

    const renderNotes = () => {
        if (!notesHtml) return <p>No study notes available.</p>;
        return <div dangerouslySetInnerHTML={{ __html: notesHtml }} />;
    };

    if (loading) {
        return (
            <motion.div 
                className={styles.loading}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <div className={styles.loadingSpinner} />
                <p>Loading your study materials...</p>
            </motion.div>
        );
    }

    if (!cards || cards.length === 0) {
        return <div className={styles.error}>No questions available. Please try again.</div>;
    }

    return (
        <div className={styles.container}>
            <motion.div 
                className={styles.notesSection}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
            >
                <h2 className={styles.sectionTitle}>Study Notes</h2>
                <div className={styles.notes}>
                    {renderNotes()}
                </div>
            </motion.div>

            <motion.div 
                className={styles.quizSection}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
            >
                <h2 className={styles.sectionTitle}>Knowledge Check</h2>
                <AnimatePresence mode="wait">
                    <motion.div className={styles.cardContainer}>
                        {cards[currentCard] && (
                            <motion.div 
                                key={currentCard}
                                className={styles.card}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                <p className={styles.cardQuestion}>{cards[currentCard].question}</p>
                                <div className={styles.cardActions}>
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => handleSwipe(false)}
                                        className={`${styles.actionButton} ${styles.falseButton}`}
                                    >
                                        <ChevronLeft size={24} />
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => handleSwipe(true)}
                                        className={`${styles.actionButton} ${styles.trueButton}`}
                                    >
                                        <ChevronRight size={24} />
                                    </motion.button>
                                </div>
                            </motion.div>
                        )}
                    </motion.div>
                </AnimatePresence>
                <motion.div 
                    className={styles.score}
                    animate={{ scale: score > currentCard / 2 ? [1, 1.1, 1] : 1 }}
                >
                    <p>Score: {score} / {currentCard}</p>
                    {currentCard === cards.length - 1 && (
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            Quiz completed! Final score: {score} / {cards.length}
                        </motion.p>
                    )}
                </motion.div>
            </motion.div>
        </div>
    );
};

export default StudyInterface;